import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] ' +
    'font-medium whitespace-nowrap transition-colors duration-150 ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    '[&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary/90 ' +
          'shadow-[0_0_16px_-6px_var(--primary)]',
        outline: 'border border-border-strong bg-surface text-foreground hover:bg-muted',
        ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
        danger: 'bg-danger text-white hover:bg-danger/90',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-[0.8125rem]',
        md: 'h-9 px-4 text-sm',
        lg: 'h-10 px-5 text-sm',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Hover/focus tooltip. Explicit text wins; otherwise an icon-only button
   * falls back to its own `aria-label` — icon buttons carry no visible text,
   * so a tooltip is nearly always wanted. Pass `tooltip={null}` to opt out.
   */
  tooltip?: ReactNode;
}

export function Button({ className, variant, size, asChild, tooltip, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  const el = <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;

  const label =
    tooltip !== undefined
      ? tooltip
      : size === 'icon' && typeof props['aria-label'] === 'string'
        ? props['aria-label']
        : null;

  if (label == null || label === '') return el;

  // A disabled <button> fires no pointer events, so Radix can't open a
  // tooltip anchored on it — which is exactly when "why is this disabled?"
  // matters most. Anchor on a focusable wrapper span in that case.
  const trigger = props.disabled ? (
    <span tabIndex={0} className="inline-flex">
      {el}
    </span>
  ) : (
    el
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export { buttonVariants };
